import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { withAlpha } from '../theme';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { notificationVisual } from '../config/notificationVisuals';

export interface Notification {
  _id: string;
  type: string;
  title: string;
  body: string;
  data?: Record<string, any>;
  clickAction?: string;
  priority: string;
  createdAt: string;
  read: boolean;
  readAt?: string;
}

interface NotificationItemProps {
  notification: Notification;
  onPress: (notification: Notification) => void;
  onDelete?: (notificationId: string) => void;
  /** Tighter rows for the header dropdown. */
  compact?: boolean;
}

/** "Just now", "5m", "3h", "Yesterday", then a short date — in the app language. */
export function relativeTime(iso: string, t: (k: any, p?: any) => string, locale: string): string {
  const date = new Date(iso);
  const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return t('notifications.time.now');
  if (minutes < 60) return t('notifications.time.minutes', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('notifications.time.hours', { count: hours });
  if (hours < 48) return t('notifications.time.yesterday');
  return date.toLocaleDateString(locale === 'ar' ? 'ar-EG' : 'en-GB', { day: 'numeric', month: 'short' });
}

/** One notification row: type icon, title, text, time, unread dot. */
export const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onPress, onDelete, compact }) => {
  const styles = useThemedStyles(getStyles);
  const { t, locale } = useLocale();
  const rtl = useRTLStyles();
  const visual = notificationVisual(notification.type, notification.data);
  const unread = !notification.read;

  return (
    <TouchableOpacity
      style={[styles.row, { flexDirection: rtl.rowDirection }, compact && styles.rowCompact, unread && styles.rowUnread]}
      onPress={() => onPress(notification)}
      activeOpacity={0.75}
      accessibilityRole="button"
      accessibilityState={{ selected: unread }}
    >
      <View style={[styles.icon, { backgroundColor: withAlpha(visual.color, 0.14) }]}>
        <Ionicons name={visual.icon as any} size={compact ? 18 : 20} color={visual.color} />
      </View>
      <View style={styles.body}>
        <View style={[styles.topLine, { flexDirection: rtl.rowDirection }]}>
          <Text style={[styles.title, unread && styles.titleUnread, { textAlign: rtl.textStart }]} numberOfLines={1}>
            {notification.title}
          </Text>
          <Text style={styles.time}>{relativeTime(notification.createdAt, t, locale)}</Text>
        </View>
        {!!notification.body && (
          <Text style={[styles.text, { textAlign: rtl.textStart }]} numberOfLines={compact ? 2 : 3}>{notification.body}</Text>
        )}
      </View>
      <View style={styles.side}>
        {unread && <View style={styles.dot} />}
        {onDelete && !compact && (
          <TouchableOpacity
            onPress={(e: any) => { e?.stopPropagation?.(); onDelete(notification._id); }}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel={t('notifications.delete')}
          >
            <Ionicons name="trash-outline" size={16} color={colors.text.secondary as string} />
          </TouchableOpacity>
        )}
      </View>
    </TouchableOpacity>
  );
};

const getStyles = () => StyleSheet.create({
  row: {
    alignItems: 'flex-start',
    gap: spacing.sm + 2,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  rowCompact: { paddingVertical: spacing.sm + 2 },
  rowUnread: { backgroundColor: withAlpha(colors.primary as string, 0.05) },
  icon: { width: 38, height: 38, borderRadius: 19, alignItems: 'center', justifyContent: 'center' },
  body: { flex: 1, minWidth: 0, gap: 2 },
  topLine: { alignItems: 'center', gap: spacing.sm },
  title: { flex: 1, fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.medium, color: colors.text.primary },
  titleUnread: { fontFamily: typography.fontFamily.semibold },
  time: { fontSize: typography.fontSize.xs, color: colors.text.secondary },
  text: { fontSize: typography.fontSize.sm, lineHeight: 19, color: colors.text.secondary },
  side: { alignItems: 'center', gap: spacing.sm, paddingTop: 4, minWidth: 16 },
  dot: { width: 8, height: 8, borderRadius: 4, backgroundColor: colors.primary },
});
